import apiService from './api'

export type EmailDeliveryStatus =
  | 'PENDING'
  | 'SENT'
  | 'DELIVERED'
  | 'OPENED'
  | 'CLICKED'
  | 'BOUNCED'
  | 'DROPPED'
  | 'DEFERRED'
  | 'SPAM_REPORT'
  | 'UNSUBSCRIBED'
  | 'FAILED'

export type EmailType =
  | 'USER_REGISTRATION'
  | 'PASSWORD_RESET'
  | 'PASSWORD_CHANGE'
  | 'ENROLLMENT_NOTIFICATION'
  | 'DEADLINE_REMINDER'
  | 'TEST_COMPLETION'
  | 'GENERAL'

export interface EmailDeliveryLog {
  id: string
  tenantId: string
  userId?: string
  emailAddress: string
  emailType: EmailType
  sendgridMessageId?: string
  subject: string
  status: EmailDeliveryStatus
  sentAt?: string
  deliveredAt?: string
  openedAt?: string
  clickedAt?: string
  bouncedAt?: string
  failedAt?: string
  errorCode?: string
  errorMessage?: string
  bounceType?: string
  bounceReason?: string
  userLoggedIn: boolean
  userLoggedInAt?: string
  webhookEvents?: unknown[]
  lastWebhookAt?: string
  deliveryTimeoutAt?: string
  isTimedOut: boolean
  metadata?: Record<string, unknown>
  createdAt: string
  updatedAt: string
  user?: {
    id: string
    name: string
    email: string
    department?: string
    position?: string
    firstLogin?: boolean
    createdAt?: string
  }
}

export interface EmailDeliveryStats {
  total: number
  pending: number
  sent: number
  delivered: number
  opened: number
  clicked: number
  bounced: number
  failed: number
  loggedIn: number
}

export interface RegistrationSummary {
  totalSent: number
  delivered: number
  opened: number
  loggedIn: number
  notLoggedIn: number
  problematic: number
}

export interface EmailDeliveryLogsResponse {
  logs: EmailDeliveryLog[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  stats: EmailDeliveryStats
}

export interface EmailDeliveryFilters {
  page?: number
  limit?: number
  status?: EmailDeliveryStatus
  emailType?: EmailType
  search?: string
  startDate?: string
  endDate?: string
}

export const EMAIL_DELIVERY_STATUS_LABELS: Record<EmailDeliveryStatus, string> = {
  PENDING: '送信準備中',
  SENT: '送信済み',
  DELIVERED: '配信完了',
  OPENED: '開封済み',
  CLICKED: 'クリック済み',
  BOUNCED: 'バウンス',
  DROPPED: '配信拒否',
  DEFERRED: '遅延',
  SPAM_REPORT: 'スパム報告',
  UNSUBSCRIBED: '配信停止',
  FAILED: '失敗',
}

export const EMAIL_TYPE_LABELS: Record<EmailType, string> = {
  USER_REGISTRATION: '新規登録',
  PASSWORD_RESET: 'パスワードリセット',
  PASSWORD_CHANGE: 'パスワード変更',
  ENROLLMENT_NOTIFICATION: 'テスト割り当て',
  DEADLINE_REMINDER: '期限リマインダー',
  TEST_COMPLETION: 'テスト完了',
  GENERAL: '一般',
}

export const EMAIL_DELIVERY_STATUS_COLORS: Record<
  EmailDeliveryStatus,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  PENDING: 'default',
  SENT: 'primary',
  DELIVERED: 'success',
  OPENED: 'success',
  CLICKED: 'info',
  BOUNCED: 'error',
  DROPPED: 'error',
  DEFERRED: 'warning',
  SPAM_REPORT: 'error',
  UNSUBSCRIBED: 'warning',
  FAILED: 'error',
}

class EmailDeliveryServiceClass {
  /**
   * メール配信ログ一覧取得
   */
  async getDeliveryLogs(filters: EmailDeliveryFilters = {}): Promise<EmailDeliveryLogsResponse> {
    const params = new URLSearchParams()
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())
    if (filters.status) params.append('status', filters.status)
    if (filters.emailType) params.append('emailType', filters.emailType)
    if (filters.search) params.append('search', filters.search)
    if (filters.startDate) params.append('startDate', filters.startDate)
    if (filters.endDate) params.append('endDate', filters.endDate)

    return apiService.get<EmailDeliveryLogsResponse>(`/email-delivery/logs?${params.toString()}`)
  }

  /**
   * 配信ログ詳細取得
   */
  async getDeliveryLogDetail(logId: string): Promise<EmailDeliveryLog> {
    return apiService.get<EmailDeliveryLog>(`/email-delivery/logs/${logId}`)
  }

  /**
   * 配信統計取得
   */
  async getDeliveryStats(): Promise<EmailDeliveryStats> {
    return apiService.get<EmailDeliveryStats>('/email-delivery/stats')
  }

  /**
   * ユーザー登録メールのサマリー取得
   */
  async getRegistrationSummary(): Promise<RegistrationSummary> {
    return apiService.get<RegistrationSummary>('/email-delivery/registration-summary')
  }

  /**
   * 問題のあるメール一覧取得
   */
  async getProblematicEmails(): Promise<{ emails: EmailDeliveryLog[] }> {
    return apiService.get<{ emails: EmailDeliveryLog[] }>('/email-delivery/problematic')
  }

  /**
   * タイムアウトチェック実行
   */
  async checkTimeouts(): Promise<{ message: string; timedOutCount: number }> {
    return apiService.post<{ message: string; timedOutCount: number }>(
      '/email-delivery/check-timeouts'
    )
  }
}

export const emailDeliveryService = new EmailDeliveryServiceClass()
export default emailDeliveryService
